/**
 * 序號兌換點數(R276 建;R277 對帳 BE 真契約)— wire DTO + domain + mapper
 * -----------------------------------------------------------------------------
 * 契約 SoT = BE `docs/99-frontend-redeem-code.md`(`dev-update-v17-1`,
 * pipeline #6601 deploy-dev success;BE `app/redeem/schemas.py`)。
 *
 * 業務語意:
 *   - admin 批次產碼(N 組 × 每組 N 點;count ≤500 / credits ≤9999)+ 歷史清單 + 作廢。
 *   - 使用者輸入序號 → 點數存入**當下工作區**錢包(`X-Current-Group`)。
 *     **沒有全域個人點數**;切錯工作區點會進錯錢包且無轉移(owner 拍板)→ 兌換介面
 *     必須顯眼標示當前工作區,回應帶 `group_id` 供成功畫面再確認。
 *   - **一碼一次**(全域,誰先兌誰拿到);同一人重送同一碼 = 冪等 200(不重複入帳)。
 *   - 序號正規形 16 字元(`ABCDEFGHJKMNPQRSTVWXYZ0123456789`,無 I/L/O/U),顯示格式
 *     `XXXX-XXXX-XXXX-XXXX`。**輸入不必轉換** —— BE 自動 strip 空白/dash、轉大寫、
 *     救援 O→0 / I→1 / L→1;FE 原樣送 trim 過的字串即可。
 *   - **R391(2026-09-25 起)兌換點數帶效期**(docs/99 D8):產碼可帶
 *     `points_valid_days`(缺 = 30、上限 365;**null → 422,沒有永久選項**),兌換
 *     回應 / 清單多 `points_expire_on`(台北日期,**含當天**的最後可用日;null =
 *     本案前的舊碼 = 永久點)。錢包側(`/me` `expiring` / 用量% 扣 T)見 types/wallet.ts。
 */

// ─── wire DTOs(對齊 BE schemas.py)────────────────────────────────────────────

/** 清單狀態(BE `RedeemCodeStatus` Literal)。 */
export type RedeemCodeStatus = 'UNUSED' | 'USED' | 'VOID';

export const REDEEM_CODE_STATUSES: readonly RedeemCodeStatus[] = ['UNUSED', 'USED', 'VOID'];

/** `GET /admin/redeem-codes` 清單單筆(BE `RedeemCodeItem`)。 */
export interface RedeemCodeItemDto {
  /** 顯示格式(`XXXX-XXXX-XXXX-XXXX`)。 */
  code: string;
  credits: number;
  status: RedeemCodeStatus;
  note?: string | null;
  created_at?: string | null;
  expires_at?: string | null;
  /** 被誰兌走(email;未兌換 / user 已刪 → null)。 */
  redeemed_by_email?: string | null;
  redeemed_at?: string | null;
  /** R391 — 兌換後點數使用效期(天);與 minutes 皆 null = 舊碼(永久點)。 */
  points_valid_days?: number | null;
  /** R391.2 — D9 分鐘級效期(1–525600);天級碼 → null。 */
  points_valid_minutes?: number | null;
  /** R391 — 兌換後點數「有效至」(台北日期,含當天);未兌換 → null。 */
  points_expire_on?: string | null;
  /** R391.2 — 失效瞬間(UTC 精確時刻);顯示規則走 `pointsExpiryDisplay`(台北午夜 = 天級)。 */
  points_expire_at?: string | null;
}

/**
 * `RedeemCodeItemDto` 鍵名 runtime 清單 —— 契約鎖比對用(BE `RedeemCodeItem.model_fields`,
 * 方向「BE ⊆ FE」;對齊 `STEP_DTO_KEYS` 慣例)。
 */
export const REDEEM_CODE_ITEM_DTO_KEYS = [
  'code', 'credits', 'status', 'note', 'created_at', 'expires_at',
  'redeemed_by_email', 'redeemed_at', 'points_valid_days', 'points_valid_minutes',
  'points_expire_on', 'points_expire_at',
] as const;

type _MissingRedeemItemKey = Exclude<
  keyof RedeemCodeItemDto,
  (typeof REDEEM_CODE_ITEM_DTO_KEYS)[number]
>;
const _redeemItemKeysExhaustive: _MissingRedeemItemKey extends never ? true : never = true;
void _redeemItemKeysExhaustive;

/** `GET /admin/redeem-codes` 的批次統計(帶 `?batch_id=` 才回)。 */
export interface RedeemBatchStatsDto {
  unused: number;
  used: number;
  void: number;
}

export interface RedeemCodeListDto {
  codes: RedeemCodeItemDto[];
  next_cursor?: string | null;
  batch_stats?: RedeemBatchStatsDto | null;
}

/** `POST /admin/redeem-codes` 回應(BE `CreateBatchResponse`)—— `codes` 是**字串**清單。 */
export interface RedeemBatchCreateResultDto {
  batch_id: string;
  credits: number;
  count: number;
  /** 顯示格式;明文存庫 —— 這頁關掉沒存也沒關係,清單端點撈得回。 */
  codes: string[];
  expires_at?: string | null;
  /** R391 — 回填確認本批效期(天);**分鐘級批 → null**;舊 BE 缺欄 → null。 */
  points_valid_days?: number | null;
  /** R391.2 — 回填確認本批效期(分鐘);**天級批 → null**。 */
  points_valid_minutes?: number | null;
}

/** `POST /redeem` 回應(BE `RedeemResponse`)。 */
export interface RedeemResultDto {
  /** 本次入帳點數(= 碼面值)。 */
  credits: number;
  /** 兌換後該工作區錢包 balance(FE 不顯數字;錢包刷新走 `GET /me`)。 */
  balance_after: number;
  /** 兌進哪個工作區 —— 成功畫面用它再確認「已存入〈工作區名〉」。 */
  group_id: string;
  /** R391 — 這批點數「有效至」(台北日期,含當天);null = 舊碼永久點 → 不顯效期。 */
  points_expire_on?: string | null;
  /** R391.2 — 失效瞬間(UTC;天級碼 = 台北午夜、分鐘級 = 兌換時刻 + N 分)。 */
  points_expire_at?: string | null;
}

/** `RedeemResultDto` / `RedeemBatchCreateResultDto` 鍵名(契約鎖;BE ⊆ FE)。 */
export const REDEEM_RESULT_DTO_KEYS = [
  'credits', 'balance_after', 'group_id', 'points_expire_on', 'points_expire_at',
] as const;
export const REDEEM_BATCH_CREATE_DTO_KEYS = [
  'batch_id', 'credits', 'count', 'codes', 'expires_at', 'points_valid_days',
  'points_valid_minutes',
] as const;

type _MissingRedeemResultKey = Exclude<
  keyof RedeemResultDto,
  (typeof REDEEM_RESULT_DTO_KEYS)[number]
>;
const _redeemResultKeysExhaustive: _MissingRedeemResultKey extends never ? true : never = true;
void _redeemResultKeysExhaustive;

type _MissingBatchCreateKey = Exclude<
  keyof RedeemBatchCreateResultDto,
  (typeof REDEEM_BATCH_CREATE_DTO_KEYS)[number]
>;
const _batchCreateKeysExhaustive: _MissingBatchCreateKey extends never ? true : never = true;
void _batchCreateKeysExhaustive;

// ─── domain ───────────────────────────────────────────────────────────────────

export interface RedeemCodeRow {
  code: string;
  credits: number;
  status: RedeemCodeStatus;
  note: string | null;
  createdAt: string | null;
  /** 序號本身的兌換期限(≠ 兌入點數的效期)。 */
  expiresAt: string | null;
  redeemedByEmail: string | null;
  redeemedAt: string | null;
  /** 兌換後點數使用效期(天);與 minutes 皆 null = 舊碼(永久點)。 */
  pointsValidDays: number | null;
  /** D9 分鐘級效期;天級碼 → null。 */
  pointsValidMinutes: number | null;
  /** 兌換後點數「有效至」(台北日期,含當天);未兌換 / 舊碼 → null。 */
  pointsExpireOn: string | null;
  /** 失效瞬間(UTC);顯示一律走 `pointsExpiryDisplay`(單點),不要自己比對午夜。 */
  pointsExpireAt: string | null;
}

export interface RedeemBatchStats {
  unused: number;
  used: number;
  voided: number;
}

export interface RedeemCodeList {
  codes: RedeemCodeRow[];
  /** null = 已翻完(判準是它,不是「筆數 < limit」— R241 B2)。 */
  nextCursor: string | null;
  /** 帶 `batchId` 篩選才有。 */
  batchStats: RedeemBatchStats | null;
}

export interface RedeemBatchCreateResult {
  batchId: string;
  credits: number;
  count: number;
  codes: string[];
  expiresAt: string | null;
  /** 本批效期(天);分鐘級批 / 舊 BE 缺欄 → null。 */
  pointsValidDays: number | null;
  /** 本批效期(分鐘);天級批 → null。 */
  pointsValidMinutes: number | null;
}

export interface RedeemResult {
  credits: number;
  balanceAfter: number;
  groupId: string;
  /** 這批點數「有效至」(台北日期,含當天);null(舊碼 / 舊 BE)→ 成功畫面不顯效期。 */
  pointsExpireOn: string | null;
  /** 失效瞬間(UTC);與 `pointsExpireOn` 一起交給 `pointsExpiryDisplay` 決定顯示形狀。 */
  pointsExpireAt: string | null;
}

// ─── mappers(邊界 normalize)────────────────────────────────────────────────────

export function mapRedeemCodeItemDto(dto: RedeemCodeItemDto): RedeemCodeRow {
  return {
    code: dto.code,
    credits: dto.credits,
    status: dto.status,
    note: dto.note ?? null,
    createdAt: dto.created_at ?? null,
    expiresAt: dto.expires_at ?? null,
    redeemedByEmail: dto.redeemed_by_email ?? null,
    redeemedAt: dto.redeemed_at ?? null,
    pointsValidDays: dto.points_valid_days ?? null,
    pointsValidMinutes: dto.points_valid_minutes ?? null,
    pointsExpireOn: dto.points_expire_on ?? null,
    pointsExpireAt: dto.points_expire_at ?? null,
  };
}

export function mapRedeemCodeListDto(dto: RedeemCodeListDto): RedeemCodeList {
  const stats = dto.batch_stats;
  return {
    codes: (dto.codes ?? []).map(mapRedeemCodeItemDto),
    nextCursor: dto.next_cursor ?? null,
    batchStats: stats ? { unused: stats.unused, used: stats.used, voided: stats.void } : null,
  };
}

export function mapRedeemBatchCreateResultDto(
  dto: RedeemBatchCreateResultDto,
): RedeemBatchCreateResult {
  return {
    batchId: dto.batch_id,
    credits: dto.credits,
    count: dto.count,
    codes: dto.codes ?? [],
    expiresAt: dto.expires_at ?? null,
    pointsValidDays: dto.points_valid_days ?? null,
    pointsValidMinutes: dto.points_valid_minutes ?? null,
  };
}

export function mapRedeemResultDto(dto: RedeemResultDto): RedeemResult {
  return {
    credits: dto.credits,
    balanceAfter: dto.balance_after,
    groupId: dto.group_id,
    pointsExpireOn: dto.points_expire_on ?? null,
    pointsExpireAt: dto.points_expire_at ?? null,
  };
}
